import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, FileText, CreditCard, Landmark, Upload, CheckCircle2, XCircle, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { myBilling, openAuthed, fmtMoney, StatusPill } from "@/lib/billingApi";
import { useBT } from "@/i18n/billingText";

const card = "rounded-2xl border border-slate-800 bg-slate-900/50 p-5";
const err = (e) => toast.error(e?.response?.data?.detail?.toString?.() || "Error");

const ProposalCard = ({ p, onDone }) => {
  const { bt } = useBT();
  const { user } = useAuth();
  const [mode, setMode] = useState(null);
  const [name, setName] = useState(user?.name || "");
  const [agree, setAgree] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onDone(); } catch (e) { err(e); } finally { setBusy(false); }
  };
  return (
    <div className={card} data-testid={`my-proposal-${p.status}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs text-slate-400">{bt("proposals").replace(/s$/, "")} {p.number}</p>
          <h3 className="text-lg font-semibold">{p.title}</h3>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-400">
            <StatusPill status={p.status} label={bt(`st.${p.status}`)} />
            {p.status === "sent" && p.valid_until && <span>{bt("validUntil")} {p.valid_until.slice(0, 10)}</span>}
          </div>
        </div>
        <p className="text-2xl font-bold tabular-nums">{fmtMoney(p.total, p.currency)}</p>
      </div>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <tbody>
            {p.lines.map((l, i) => (
              <tr key={i} className="border-t border-slate-800/70">
                <td className="py-1.5 pr-3">{l.description}{l.qty !== 1 ? <span className="text-slate-500"> × {l.qty}</span> : null}</td>
                <td className="py-1.5 text-right tabular-nums whitespace-nowrap">{fmtMoney(l.net, p.currency)}</td>
              </tr>
            ))}
            {p.vat_total > 0 && (
              <tr className="border-t border-slate-800/70 text-slate-400"><td className="py-1.5">{bt("vat").replace(" %", "")}</td><td className="py-1.5 text-right tabular-nums">{fmtMoney(p.vat_total, p.currency)}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {p.notes && <p className="mt-3 whitespace-pre-wrap text-sm text-slate-300">{p.notes}</p>}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="border-slate-700 bg-slate-900" onClick={() => openAuthed(`/account/billing/proposals/${p.id}/pdf`).catch(err)}>
          <FileText className="h-4 w-4 mr-1" />{bt("download")} PDF
        </Button>
        {p.contract_id && (
          <Button size="sm" variant="outline" className="border-venka-green/40 bg-slate-900 text-venka-green" data-testid="my-contract"
            onClick={() => openAuthed(`/account/billing/proposals/${p.id}/contract.pdf`).catch(err)}>
            <ScrollText className="h-4 w-4 mr-1" />{bt("contract")} {p.contract_number}
          </Button>
        )}
        {p.status === "sent" && !mode && (
          <>
            <Button size="sm" className="bg-venka-green text-slate-950 hover:bg-venka-green-light" onClick={() => setMode("accept")} data-testid="my-proposal-accept"><CheckCircle2 className="h-4 w-4 mr-1" />{bt("accept")}</Button>
            <Button size="sm" variant="ghost" className="text-slate-300" onClick={() => setMode("decline")} data-testid="my-proposal-decline"><XCircle className="h-4 w-4 mr-1" />{bt("decline")}</Button>
          </>
        )}
      </div>
      {mode === "accept" && p.status === "sent" && (
        <div className="mt-4 space-y-3 rounded-xl border border-venka-green/30 bg-venka-green/5 p-4">
          <label className="block text-xs text-slate-400">{bt("yourName")}
            <Input className="mt-1 bg-slate-950 border-slate-700" value={name} onChange={(e) => setName(e.target.value)} data-testid="my-accept-name" />
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-1" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="my-accept-agree" />
            <span>{bt("acceptConfirm")}</span>
          </label>
          <div className="flex gap-2">
            <Button size="sm" className="bg-venka-green text-slate-950 hover:bg-venka-green-light" disabled={!agree || name.trim().length < 3 || busy}
              onClick={() => run(() => myBilling.accept(p.id, name.trim()), bt("accepted"))} data-testid="my-accept-confirm">
              {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}{bt("accept")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setMode(null)}>{bt("cancel")}</Button>
          </div>
        </div>
      )}
      {mode === "decline" && p.status === "sent" && (
        <div className="mt-4 space-y-3 rounded-xl border border-slate-700 p-4">
          <Textarea rows={3} className="bg-slate-950 border-slate-700" placeholder={bt("declineReason")} value={reason} onChange={(e) => setReason(e.target.value)} />
          <div className="flex gap-2">
            <Button size="sm" className="bg-red-500 text-white hover:bg-red-400" disabled={busy} onClick={() => run(() => myBilling.decline(p.id, reason), bt("declined"))}>{bt("decline")}</Button>
            <Button size="sm" variant="ghost" onClick={() => setMode(null)}>{bt("cancel")}</Button>
          </div>
        </div>
      )}
    </div>
  );
};

const InvoiceCard = ({ inv, onDone }) => {
  const { bt } = useBT();
  const [showTransfer, setShowTransfer] = useState(false);
  const [amount, setAmount] = useState(inv.balance);
  const [ref, setRef] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const open = ["open", "partially_paid"].includes(inv.status);
  const pending = (inv.payments || []).filter((p) => p.status === "pending_verification");
  const bank = inv.pay_options?.bank || {};
  const payCard = async () => {
    setBusy(true);
    try { const r = await myBilling.payCard(inv.id); window.location.href = r.checkout_url; } catch (e) { err(e); setBusy(false); }
  };
  const sendProof = async () => {
    if (!file) return;
    setBusy(true);
    try {
      await myBilling.uploadProof(inv.id, amount, ref, file);
      toast.success(bt("proofSent"));
      setShowTransfer(false);
      onDone();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  return (
    <div className={card} data-testid={`my-invoice-${inv.status}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs text-slate-400">{bt(`kind.${inv.kind}`)}</p>
          <h3 className="text-lg font-semibold">{inv.number}</h3>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-400">
            <StatusPill status={inv.status} label={bt(`st.${inv.status}`)} />
            {inv.kind !== "credit_note" && <span>{bt("due")} {(inv.due_at || "").slice(0, 10)}</span>}
            {inv.fiscal_invoice_number && <span>AGT {inv.fiscal_invoice_number}</span>}
          </div>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold tabular-nums">{fmtMoney(inv.kind === "credit_note" ? inv.totals?.total : inv.balance, inv.currency)}</p>
          {inv.kind !== "credit_note" && <p className="text-xs text-slate-400">{bt("total")} {fmtMoney(inv.totals?.total, inv.currency)}{inv.amount_paid ? ` · ${bt("paid")} ${fmtMoney(inv.amount_paid, inv.currency)}` : ""}</p>}
        </div>
      </div>
      {pending.length > 0 && <p className="mt-3 text-sm text-amber-300">{bt("st.pending_verification")}: {pending.map((p) => fmtMoney(p.amount, p.currency)).join(", ")}</p>}
      {(inv.payments || []).filter((p) => p.status === "confirmed").length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {(inv.payments || []).filter((p) => p.status === "confirmed").map((p) => (
            <button key={p.id} type="button" onClick={() => openAuthed(`/account/billing/payments/${p.id}/receipt.pdf`).catch(err)}
              className="inline-flex items-center gap-1.5 rounded-full border border-green-500/30 bg-green-500/10 px-3 py-1 text-xs text-green-300" data-testid="my-receipt">
              <FileText className="h-3.5 w-3.5" />{bt("receipt")} · {fmtMoney(p.amount, p.currency)}
            </button>
          ))}
        </div>
      )}
      {(inv.payments || []).filter((p) => p.status === "rejected").slice(0, 1).map((p) => (
        <p key={p.id} className="mt-2 text-sm text-red-300">{bt("st.rejected")}: {p.reject_reason}</p>
      ))}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="border-slate-700 bg-slate-900" onClick={() => openAuthed(`/account/billing/invoices/${inv.id}/pdf`).catch(err)}>
          <FileText className="h-4 w-4 mr-1" />{bt("download")} PDF
        </Button>
        {open && inv.pay_options?.card && (
          <Button size="sm" className="bg-venka-green text-slate-950 hover:bg-venka-green-light" disabled={busy} onClick={payCard} data-testid="my-pay-card">
            <CreditCard className="h-4 w-4 mr-1" />{bt("payCard")}
          </Button>
        )}
        {open && (
          <Button size="sm" variant="outline" className="border-slate-700 bg-slate-900" onClick={() => setShowTransfer((v) => !v)} data-testid="my-pay-transfer">
            <Landmark className="h-4 w-4 mr-1" />{bt("payTransfer")}
          </Button>
        )}
      </div>
      {showTransfer && (
        <div className="mt-4 space-y-3 rounded-xl border border-slate-700 p-4">
          <p className="text-sm text-slate-300">{bt("transferInfo")}</p>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            {bank.bank_name && <><dt className="text-slate-400">{bt("bankName")}</dt><dd>{bank.bank_name}</dd></>}
            {bank.account_holder && <><dt className="text-slate-400">{bt("accountHolder")}</dt><dd>{bank.account_holder}</dd></>}
            {bank.iban && <><dt className="text-slate-400">{bt("iban")}</dt><dd className="font-mono">{bank.iban}</dd></>}
            {bank.swift && <><dt className="text-slate-400">{bt("swift")}</dt><dd className="font-mono">{bank.swift}</dd></>}
            <dt className="text-slate-400">{bt("reference")}</dt><dd className="font-mono font-semibold">{inv.number}</dd>
            <dt className="text-slate-400">{bt("amount")}</dt><dd className="font-semibold">{fmtMoney(inv.balance, inv.currency)}</dd>
          </dl>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs text-slate-400">{bt("amountPaid")} ({inv.currency})
              <Input type="number" min="0" step="0.01" className="mt-1 bg-slate-950 border-slate-700" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="my-proof-amount" />
            </label>
            <label className="text-xs text-slate-400">{bt("reference")}
              <Input className="mt-1 bg-slate-950 border-slate-700" value={ref} onChange={(e) => setRef(e.target.value)} />
            </label>
          </div>
          <input type="file" accept=".pdf,image/jpeg,image/png,image/webp" className="block w-full text-sm" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="my-proof-file" />
          <Button size="sm" className="bg-venka-green text-slate-950 hover:bg-venka-green-light" disabled={!file || busy || !(Number(amount) > 0)} onClick={sendProof} data-testid="my-proof-send">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}{busy ? bt("sending") : bt("uploadProof")}
          </Button>
        </div>
      )}
    </div>
  );
};

export const CustomerBilling = () => {
  const { bt, lang } = useBT();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const load = useCallback(() => myBilling.get().then(setData).catch(() => setData({ proposals: [], invoices: [] })), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (params.get("paid")) toast.success(bt("paymentThanks")); }, [params]);

  if (!data) return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-venka-green-light" /></div>;
  const empty = data.proposals.length === 0 && data.invoices.length === 0;
  return (
    <div className="space-y-6" data-testid="customer-billing">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{bt("myBilling")}</h1>
          <p className="text-sm text-slate-400">{bt("myBillingSub")}</p>
        </div>
        <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/public/guide.pdf?lang=${lang}`} target="_blank" rel="noreferrer" data-testid="customer-guide"
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-300 hover:border-venka-green-light hover:text-white">
          <FileText className="h-3.5 w-3.5" />{({ pt: "Guia do cliente", fr: "Guide client" })[lang] || "Customer guide"}
        </a>
      </div>
      {empty && <p className="rounded-2xl border border-slate-800 p-8 text-center text-slate-400">{bt("nothingYet")}</p>}
      {data.proposals.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm uppercase tracking-wider text-slate-500">{bt("proposals")}</h2>
          {data.proposals.map((p) => <ProposalCard key={p.id} p={p} onDone={load} />)}
        </section>
      )}
      {data.invoices.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm uppercase tracking-wider text-slate-500">{bt("invoices")}</h2>
          {data.invoices.map((i) => <InvoiceCard key={i.id} inv={i} onDone={load} />)}
        </section>
      )}
    </div>
  );
};

export default CustomerBilling;
